import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { Loader2, BookOpen, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '../ui/button';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PdfPage({ pdf, num, scale }) {
  const canvasRef = useRef(null);
  const holderRef = useRef(null);
  const [visible, setVisible] = useState(num <= 2);

  useEffect(() => {
    if (visible || !holderRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisible(true), { rootMargin: '600px' });
    io.observe(holderRef.current);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let task;
    let cancelled = false;
    pdf.getPage(num).then((page) => {
      if (cancelled || !canvasRef.current) return;
      const width = holderRef.current?.clientWidth || 800;
      const base = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: (width / base.width) * scale });
      const dpr = window.devicePixelRatio || 1;
      const c = canvasRef.current;
      c.width = Math.floor(vp.width * dpr);
      c.height = Math.floor(vp.height * dpr);
      c.style.width = `${vp.width}px`;
      c.style.height = `${vp.height}px`;
      task = page.render({ canvasContext: c.getContext('2d'), viewport: vp, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null });
      task.promise.catch(() => {});
    }).catch(() => {});
    return () => { cancelled = true; task?.cancel(); };
  }, [pdf, num, scale, visible]);

  return (
    <div ref={holderRef} className="w-full flex" style={{ minHeight: visible ? undefined : 900 }} data-testid={`ncert-page-${num}`}>
      <canvas ref={canvasRef} className="mx-auto shadow-soft bg-white rounded-sm max-w-none" />
    </div>
  );
}

export function NcertPdfReader({ code, chapter, title }) {
  const [pdf, setPdf] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    let alive = true;
    let doc;
    let loadingTask;
    setPdf(null);
    setError(null);
    (async () => {
      try {
        const token = localStorage.getItem('sip_token');
        const res = await fetch(`${API_URL}/api/library/ncert-pdf?code=${encodeURIComponent(code)}&chapter=${chapter}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(res.status === 404 ? 'This chapter is not available.' : `Could not load chapter (${res.status})`);
        const data = new Uint8Array(await res.arrayBuffer());
        if (!alive) return;
        loadingTask = pdfjsLib.getDocument({ data });
        doc = await loadingTask.promise;
        if (alive) setPdf(doc);
      } catch (e) {
        if (alive) setError(e.message || 'Could not load chapter');
      }
    })();
    return () => { alive = false; (doc || loadingTask)?.destroy()?.catch?.(() => {}); };
  }, [code, chapter, attempt]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3" data-testid="ncert-reader-error">
        <BookOpen className="w-10 h-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button size="sm" variant="outline" onClick={() => setAttempt((a) => a + 1)} data-testid="ncert-reader-retry">Try again</Button>
      </div>
    );
  }
  if (!pdf) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3" data-testid="ncert-reader-loading">
        <Loader2 className="w-8 h-8 animate-spin text-ae-blue" />
        <p className="text-sm text-muted-foreground">Loading Chapter {chapter}…</p>
      </div>
    );
  }
  return (
    <div className="relative h-full" data-testid="ncert-reader">
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-slate-200/80 dark:border-slate-700 p-1 shadow-soft">
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(1)))} data-testid="ncert-zoom-out" aria-label="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
        <span className="text-[11px] w-10 text-center tabular-nums" data-testid="ncert-zoom-level">{Math.round(scale * 100)}%</span>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setScale((s) => Math.min(2.4, +(s + 0.2).toFixed(1)))} data-testid="ncert-zoom-in" aria-label="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
      </div>
      <div className="h-full overflow-auto bg-slate-100 dark:bg-slate-950 py-4 px-2 sm:px-6 space-y-4" data-testid="ncert-reader-pages" aria-label={title}>
        {Array.from({ length: pdf.numPages }, (_, i) => (
          <PdfPage key={`${chapter}-${i + 1}`} pdf={pdf} num={i + 1} scale={scale} />
        ))}
      </div>
    </div>
  );
}
